// ===== Nomus — conferência de preço, bloqueio e saldo, AO VIVO =====
//
// Spec: docs/superpowers/specs/2026-09-23-consulta-real-ao-nomus.md
// Até 23/09 este arquivo SIMULAVA o Nomus (`nomusGetProduto` sorteava preço,
// saldo, ativo e bloqueio) e chegou a barrar pedido por um bloqueio sorteado.
// Agora a consulta vai à Edge Function `consultar-produtos-nomus`, e a regra de
// envio é UMA só, importada da lógica da função:
//
//   o item vai ao ERP se está ativo, não bloqueado, tem id do Nomus, foi
//   conferido e tem preço (o do fator, ou o negociado à mão).
//
// Decisão do Kaio (25/09), que substitui a de 23/09: o preço certo é o do FATOR
// do CRM; diferença com o Nomus só informa e não trava o pedido. Travam produto
// bloqueado, inativo, sem id, item sem preço e falha de consulta; preço
// negociado passa. Sem conferência, o pedido não sai.
import React from 'react';
import { Icon } from './icons.jsx';
import { ModalShell } from './modals.jsx';
import { useStore } from './store.jsx';
import { situacaoDoItem, BLOQUEIA_ENVIO } from '../supabase/functions/consultar-produtos-nomus/logica.ts';

const { useState: useStateNS, useEffect: useEffectNS, useCallback: useCallbackNS } = React;

const nsBrl = (v) => "R$ " + (Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const NS_SIT = {
  bloqueado:  { lbl: "Bloqueado para venda no Nomus", tom: "danger", icon: "ban" },
  inativo:    { lbl: "Inativo no Nomus",              tom: "danger", icon: "alert-triangle" },
  sem_id:     { lbl: "Sem id do Nomus",               tom: "danger", icon: "alert-triangle" },
  erro:       { lbl: "Não conferido",                 tom: "danger", icon: "alert-circle" },
  sem_preco:  { lbl: "Sem preço",                     tom: "danger", icon: "alert-triangle" },
  // So informam (decisao do Kaio, 25/09): o preco que vai e o do fator do CRM.
  divergente: { lbl: "Diferente do Nomus",            tom: "cool",   icon: "info" },
  nomus_sem_preco: { lbl: "Sem preço no Nomus nesta tabela", tom: "cool", icon: "info" },
  negociado:  { lbl: "Preço negociado",               tom: "cool",   icon: "pencil" },
  ok:         { lbl: "Confere com a tabela",          tom: "ok",     icon: "check" },
};

// Uma consulta para os itens todos (a função faz os lotes). Falha da consulta
// inteira vira "erro" em CADA item: sem conferência, nada passa.
async function conferirItens(consultar, itens, { idTabelaNomus = null, idEmpresa = 2 } = {}) {
  const ids = [...new Set(itens.map((it) => it.idProduto).filter(Boolean))];
  let porId = {};
  let falhaGeral = null;
  if (ids.length) {
    try {
      const resultados = await consultar(ids);
      porId = Object.fromEntries((resultados || []).map((r) => [r.idProduto, r]));
    } catch (e) {
      falhaGeral = e?.message || 'Não foi possível consultar o Nomus.';
    }
  }
  return itens.map((it) => {
    const r = falhaGeral ? { idProduto: it.idProduto, ok: false, erro: falhaGeral } : porId[it.idProduto];
    const precoNomus = r?.ok && idTabelaNomus != null ? (r.precosPorTabela[String(idTabelaNomus)] ?? null) : null;
    return {
      _id: it._id, codigo: it.codigo, nome: it.nome, idProduto: it.idProduto,
      precoCrm: it.precoUnitario, precoNomus, negociado: !!it.precoManual,
      situacao: situacaoDoItem(it, r, idTabelaNomus),
      saldo: r?.ok ? (r.saldos[String(idEmpresa)] ?? null) : null,
      atualizadoEm: r?.ok && r.alteradoEm ? new Date(r.alteradoEm) : null,
      erro: r && !r.ok ? r.erro : null,
      resultado: r || null,
      idTabelaNomus,
    };
  });
}

// ─── Selo na linha do item, dentro do builder ────────────────────────────────
// Recalcula a situação com o preço ATUAL do item: se o vendedor editar o preço
// depois da consulta, o selo acompanha.
function NomusItemSelo({ item }) {
  if (item.nomusLoading) return <span className="ns-pill loading"><Icon name="loader" size={9} /> Nomus…</span>;
  const n = item.nomus;
  if (!n) return null;
  const sit = situacaoDoItem(item, n.resultado, n.idTabelaNomus ?? null);
  const s = NS_SIT[sit];
  const pills = [
    <span key="s" className={`ns-pill ${s.tom}`} title={n.erro || undefined}>
      <Icon name={s.icon} size={9} /> {sit === 'divergente' && n.precoNomus != null ? `Nomus: ${nsBrl(n.precoNomus)}` : s.lbl}
    </span>,
  ];
  if (n.saldo != null && n.saldo <= 0) pills.push(<span key="e" className="ns-pill muted">Saldo {String(n.saldo).replace(".", ",")}</span>);
  return <div className="ns-pills">{pills}</div>;
}

// ─── Modal de conferência ────────────────────────────────────────────────────
// modo "envio": antes do POST /pedidos. Bloqueado, inativo, sem id, sem preço
//   e falha de consulta barram. Preço diferente do Nomus SO INFORMA: o que vai
//   e o preço do CRM, pelo fator (decisao do Kaio, 25/09). Ate 25/09 isso
//   barrava ate "Alinhar a tabela" -- que trocava o preço do fator pelo do Nomus.
// modo "aplicar": dentro do builder, so confere; nao muda preço.
function RevalidacaoModal({ itens, tabela, idEmpresa = 2, cta, modo = "aplicar", onClose, onConfirm }) {
  const store = useStore();
  const [fase, setFase] = useStateNS("rodando");
  const [linhas, setLinhas] = useStateNS([]);
  const [verOk, setVerOk] = useStateNS(false);
  const idTabelaNomus = tabela?.idNomus ?? null;

  const consultar = useCallbackNS(async () => {
    setFase("rodando");
    const res = await conferirItens(store.consultarProdutosNomus, itens, { idTabelaNomus, idEmpresa });
    setLinhas(res);
    setFase("pronto");
  }, [itens, idTabelaNomus, idEmpresa, store.consultarProdutosNomus]);

  useEffectNS(() => { consultar(); }, []);  // uma consulta ao abrir; "Tentar de novo" refaz

  const falhas = linhas.filter((l) => l.situacao === "erro");
  const barrados = linhas.filter((l) => ["bloqueado", "inativo", "sem_id", "sem_preco"].includes(l.situacao));
  const divergentes = linhas.filter((l) => l.situacao === "divergente" || l.situacao === "nomus_sem_preco");
  const negociados = linhas.filter((l) => l.situacao === "negociado");
  const conferem = linhas.filter((l) => l.situacao === "ok");
  const impede = modo === "envio" && linhas.some((l) => BLOQUEIA_ENVIO.has(l.situacao));

  // Sem alinhamento: nenhum preço muda aqui (o primeiro argumento segue sendo a
  // lista de ajustes, agora sempre vazia, para quem chama nao precisar mudar).
  const confirmar = () => onConfirm([], { total: linhas.length, diferentes: divergentes.length });

  const Linha = ({ l }) => {
    const s = NS_SIT[l.situacao] || NS_SIT.ok;
    return (
      <div className={`nr-row ${s.tom}`}>
        <div className="nr-check"><Icon name={s.icon} size={12} /></div>
        <div className="nr-prod">
          <div className="nr-nome"><span className="mono nr-cod">{l.codigo}</span> {l.nome}</div>
          <div className="nr-meta">
            <span className={`ns-pill ${s.tom}`}>{s.lbl}</span>
            {l.erro && <span className="nr-saldo">{l.erro}</span>}
            {l.saldo != null && <span className="nr-saldo">saldo {String(l.saldo).replace(".", ",")}</span>}
            {l.atualizadoEm && <span className="nr-saldo">tabela de {l.atualizadoEm.toLocaleDateString("pt-BR")}</span>}
          </div>
        </div>
        <div className="nr-precos">
          <span className="mono nr-de">{nsBrl(l.precoCrm)}</span>
          {l.precoNomus != null && (l.situacao === "divergente" || l.situacao === "negociado") && Math.abs(l.precoNomus - l.precoCrm) >= 0.005 && (
            <span className="mono nr-para" style={{ marginLeft: 8, color: "var(--color-ink-soft)" }}>Nomus {nsBrl(l.precoNomus)}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <ModalShell title={modo === "envio" ? "Conferir no Nomus antes do pedido" : "Revalidar preços no Nomus"} icon="server" onClose={onClose} width={780} footer={
      <>
        <div className="nr-foot-meta">
          {fase === "rodando" ? <span>consultando o Nomus…</span> : <span>{linhas.length} {linhas.length === 1 ? "item" : "itens"} · tabela {tabela?.nome || "—"}</span>}
        </div>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        {fase === "pronto" && falhas.length > 0 && (
          <button className="btn" onClick={consultar}><Icon name="refresh-cw" size={13} /> Tentar de novo</button>
        )}
        {fase === "pronto" && (impede
          ? <button className="btn primary" disabled style={{ opacity: 0.45, cursor: "not-allowed" }}><Icon name="ban" size={13} /> Pedido bloqueado</button>
          : <button className="btn primary" onClick={confirmar}><Icon name="check" size={13} /> {cta || "Confirmar"}</button>
        )}
      </>
    }>
      {fase === "rodando" && (
        <div className="nr-loading">
          <div className="nr-loading-txt">Consultando o Nomus · {itens.length} {itens.length === 1 ? "item" : "itens"}</div>
          <div className="nr-loading-sub">~0,8 s por item, cinco em paralelo</div>
        </div>
      )}

      {fase === "pronto" && (<>
        {falhas.length > 0 && (
          <div className="nr-block-warn">
            <Icon name="alert-circle" size={14} />
            <div>
              <strong>{falhas.length} item(ns) não foram conferidos.</strong>
              <p>{falhas[0].erro} {modo === "envio" ? "Sem conferência, o pedido não sai." : ""}</p>
            </div>
          </div>
        )}
        {barrados.length > 0 && (<>
          <div className="nr-block-warn" style={{ marginTop: 8 }}>
            <Icon name="ban" size={14} />
            <div>
              <strong>{barrados.length} item(ns) não pode(m) ir para o Nomus assim.</strong>
              <p>Bloqueado, inativo ou sem id: troque o produto. Sem preço: informe o preço no item.</p>
            </div>
          </div>
          {barrados.map((l) => <Linha key={l._id} l={l} />)}
        </>)}
        {falhas.map((l) => <Linha key={l._id} l={l} />)}

        {divergentes.length > 0 && (<>
          <div className="nr-sec">Preço diferente do Nomus</div>
          <div className="nr-note cool">
            <Icon name="info" size={12} /> Vai o preço do CRM, pelo fator da tabela, com os centavos dele. A diferença com o Nomus é só informação e não trava o pedido.
          </div>
          {divergentes.map((l) => <Linha key={l._id} l={l} />)}
        </>)}

        {negociados.length > 0 && (<>
          <div className="nr-note cool" style={{ marginTop: 10 }}>
            <Icon name="pencil" size={12} /> {negociados.length} item(ns) com preço negociado {negociados.length === 1 ? "vai" : "vão"} como está.
          </div>
          {negociados.map((l) => <Linha key={l._id} l={l} />)}
        </>)}

        {conferem.length > 0 && (<>
          <div className="nr-sec">
            <button className="nr-toggle" onClick={() => setVerOk((v) => !v)}>
              <Icon name={verOk ? "chevron-up" : "chevron-down"} size={12} /> {conferem.length} {conferem.length === 1 ? "item confere" : "itens conferem"} com a tabela
            </button>
          </div>
          {verOk && conferem.map((l) => <Linha key={l._id} l={l} />)}
        </>)}
      </>)}
    </ModalShell>
  );
}

export { nsBrl, conferirItens, NomusItemSelo, RevalidacaoModal };
